import React from "react";
import { getTdStyle } from "../../styles/attendanceStyles";
import StatusBadge from "./StatusBadge";
import RowActions from "./RowActions";
import OvertimeApprovalButton from "./OvertimeApprovalButton";
import { useVisibleColumns } from "../../../../hooks/useVisibleColumns";
import PunchPin from "../../../../components/RemotePunch/PunchPin";
import EmployeeCardHeader from "../../../../components/EmployeeCardHeader";
import { CalendarDays } from "lucide-react";

const getOvertimeDisplay = (value) => {
  if (!value || value === "-" || value === "0h" || value === "0m") {
    return <span style={{ color: "#9ca3af" }}>-</span>;
  }
  return <span style={{ color: "#10b981", fontWeight: "600" }}>+{value}</span>;
};

const getTardinessDisplay = (value) => {
  if (!value || value === "-" || value === "0h" || value === "0m") {
    return <span style={{ color: "#9ca3af" }}>-</span>;
  }
  return <span style={{ color: "#ef4444", fontWeight: "600" }}>-{value}</span>;
};

/**
 * Where a punch was taken, shown directly beneath its time.
 *
 * Reads as a quiet chip rather than a second value: a hairline outline and a
 * teal accent rule separate it from the timestamp above without competing with
 * it. Flat by design — no shadow, no icon — so a dense table stays scannable.
 * Renders nothing at all for records that predate punch-location capture.
 */
const PunchLocation = ({ name, theme, language }) => {
  if (!name) return null;
  const isDark = theme === "dark";
  return (
    <span
      title={language === "ar" ? `سُجّل من: ${name}` : `Punched from: ${name}`}
      style={{
        marginTop: "3px",
        maxWidth: "100%",
        display: "inline-block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: "1px 7px",
        fontFamily: "inherit",
        fontSize: "10px",
        fontWeight: 500,
        letterSpacing: "0.02em",
        lineHeight: 1.6,
        borderRadius: "3px",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.28)" : "rgba(100,116,139,0.24)"}`,
        borderInlineStart: `2px solid ${isDark ? "#38bdf8" : "#0e7490"}`,
        background: isDark ? "rgba(148,163,184,0.10)" : "rgba(15,23,42,0.035)",
        color: isDark ? "rgba(226,232,240,0.82)" : "rgba(15,23,42,0.68)",
        direction: language === "ar" ? "rtl" : "ltr",
      }}
    >
      {name}
    </span>
  );
};

const TableRow = ({
  record,
  theme,
  isRTL,
  t,
  language,
  viewType,
  companyConfigs,
  canResetCheckIn,
  canResetCheckOut,
  canRevertOriginal,
  canViewAttendance,
  // ── NEW ───────────────────────────────────────────────────────────────────
  canApproveOvertime,
  overtimeApprovalRequired,
  onApproveOvertime,
  onRejectOvertime,
  // ─────────────────────────────────────────────────────────────────────────
  getConfigForEmployee,
  getEmployeeName,
  updateAttendancein,
  updateAttendanceout,
  revertAttendanceToOriginal,
  fetchEmployeeHistory,
  showAlert,
  BASE_URL,
  asCard = false,
}) => {
  const tdStyle = getTdStyle(theme);

  // ✅ Admin-level column visibility (hiddenColumns registry). Must stay in
  // sync with the same keys gated in TableHeader.jsx (same order). Bypassed
  // roles (admin/superadmin) always see every column — the hook handles that.
  const attendanceColumns = [
    { key: "employeeName" },
    { key: "date" },
    { key: "checkIn" },
    { key: "checkOut" },
    { key: "hoursWorked" },
    { key: "status" },
    { key: "overtime" },
    { key: "tardiness" },
    { key: "client" },
    { key: "actions" },
  ];
  const visibleColumns = useVisibleColumns("attendance", "attendanceTable", attendanceColumns);
  const showColumn = (key) => visibleColumns.some((col) => col.key === key);

  const employeeName =
    getEmployeeName(record.employeeId) ||
    record.employeeName ||
    (language === "ar"
      ? record.employee?.arabicName || record.employee?.name_ar || record.employee?.sr
      : record.employee?.name || record.employee?.sr || record.employee?.fullName || record.employee?.englishName) ||
    "-";

  const rowActions = (
    <RowActions
      record={record}
      theme={theme}
      isRTL={isRTL}
      t={t}
      language={language}
      viewType={viewType}
      companyConfigs={companyConfigs}
      canResetCheckIn={canResetCheckIn}
      canResetCheckOut={canResetCheckOut}
      canRevertOriginal={canRevertOriginal}
      canViewAttendance={canViewAttendance}
      canApproveOvertime={canApproveOvertime}
      overtimeApprovalRequired={overtimeApprovalRequired}
      onApproveOvertime={onApproveOvertime}
      onRejectOvertime={onRejectOvertime}
      getConfigForEmployee={getConfigForEmployee}
      updateAttendancein={updateAttendancein}
      updateAttendanceout={updateAttendanceout}
      revertAttendanceToOriginal={revertAttendanceToOriginal}
      fetchEmployeeHistory={fetchEmployeeHistory}
      showAlert={showAlert}
      BASE_URL={BASE_URL}
    />
  );

  // Phones: the same record as a card — name + status, then the three
  // numbers people scan for (in / out / hours), extras only when present.
  if (asCard) {
    const isDark = theme === "dark";
    const muted = isDark ? "rgba(226,232,240,0.62)" : "#64748b";
    const strong = isDark ? "#f1f5f9" : "#0f172a";
    const hasOT = record.overTime && !["-", "0h", "0m"].includes(record.overTime);
    const hasLate = record.underTime && !["-", "0h", "0m"].includes(record.underTime);
    const cardEmail = showColumn("employeeName") ? record.employee?.email || record.email || "" : "";
    const cardDate = showColumn("date")
      ? new Date(record.date).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" })
      : "";
    const cell = (label, content) => (
      <div style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
        <span style={{ fontSize: "12px", color: muted }}>{label}</span>
        <span style={{ fontSize: "14px", fontWeight: 600, color: strong }}>{content}</span>
      </div>
    );
    const punch = (time, location, editedBy, remote, kind) => (
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px", minWidth: 0 }}>
        <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{time || "-"}</span>
        {remote
          ? <PunchPin punch={remote} kind={kind} employeeName={employeeName} theme={theme} language={language} />
          : <PunchLocation name={location} theme={theme} language={language} />}
        {editedBy && <span style={{ fontSize: "11px", fontWeight: 500, color: "#f59e0b" }}>{editedBy}</span>}
      </span>
    );
    return (
      <article
        style={{
          padding: "12px 14px",
          borderRadius: "14px",
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(15,23,42,0.08)",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <EmployeeCardHeader
          theme={theme}
          name={showColumn("employeeName") ? employeeName : ""}
          // With an email the strip shows it, so the date moves up next to the client.
          subtitle={[cardEmail && cardDate, showColumn("client") && record.clientName].filter(Boolean).join(" · ")}
          email={cardEmail}
          info={cardDate}
          infoIcon={CalendarDays}
          status={showColumn("status") ? <StatusBadge status={record.status} language={language} /> : null}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px" }}>
          {showColumn("checkIn") && cell(t.checkIn, punch(record.checkIn, record.checkInLocationName, record.editedByIn, record.remoteIn, "in"))}
          {showColumn("checkOut") && cell(t.checkOut, punch(record.checkOut, record.checkOutLocationName, record.editedByOut, record.remoteOut, "out"))}
          {showColumn("hoursWorked") &&
            cell(t.hoursWorked, record.hoursWorked > 0 ? `${Math.round(parseFloat(record.hoursWorked) * 10) / 10} ${t.hours}` : "-")}
        </div>
        {((showColumn("overtime") && (hasOT || (overtimeApprovalRequired && record.overTime))) || (showColumn("tardiness") && hasLate)) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", fontSize: "13px", color: muted }}>
            {showColumn("overtime") && (hasOT || (overtimeApprovalRequired && record.overTime)) && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                {t.overtime}:{" "}
                {overtimeApprovalRequired && (record.overTime > 0 || record.overTime) ? (
                  <OvertimeApprovalButton
                    record={record}
                    theme={theme}
                    isRTL={isRTL}
                    language={language}
                    viewType={viewType}
                    canApproveOvertime={canApproveOvertime}
                    overtimeApprovalRequired={overtimeApprovalRequired}
                    onApprove={onApproveOvertime}
                    onReject={onRejectOvertime}
                  />
                ) : (
                  getOvertimeDisplay(record.overTime)
                )}
              </span>
            )}
            {showColumn("tardiness") && hasLate && (
              <span>
                {t.tardiness}: {getTardinessDisplay(record.underTime)}
              </span>
            )}
          </div>
        )}
        {showColumn("actions") && (
          <div className="nx-attendance-card-actions" style={{ paddingTop: "10px", borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(15,23,42,0.06)" }}>
            {rowActions}
          </div>
        )}
      </article>
    );
  }

  return (
    <tr
      key={record.id}
      style={{
        borderBottom:
          theme === "dark"
            ? "1px solid rgba(59, 130, 246, 0.1)"
            : "1px solid rgba(59, 130, 246, 0.05)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background =
          theme === "dark"
            ? "rgba(59, 130, 246, 0.08)"
            : "rgba(59, 130, 246, 0.04)";
        e.currentTarget.style.boxShadow =
          theme === "dark"
            ? "inset 0 0 1rem rgba(59, 130, 246, 0.05)"
            : "inset 0 0 1rem rgba(59, 130, 246, 0.02)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* <td style={tdStyle}>{record.employeeId}</td> */}
      {showColumn("employeeName") && (
      <td style={{ ...tdStyle, fontWeight: "500",verticalAlign: isRTL ? 'right' : 'left',
      justifyContent: isRTL ? 'right' : 'left',
      textAlign: isRTL ? 'right' : 'left', }}>
        {getEmployeeName(record.employeeId) ||
          record.employeeName ||
          (language === "ar"
            ? record.employee?.arabicName || record.employee?.name_ar || record.employee?.sr
            : record.employee?.name || record.employee?.sr || record.employee?.fullName || record.employee?.englishName) ||
          "-"}
      </td>
      )}
      {showColumn("date") && (
      <td style={tdStyle}>
        {new Date(record.date).toLocaleDateString(
          language === "ar" ? "ar-EG" : "en-US",
          { year: "numeric", month: "long", day: "numeric" }
        )}
      </td>
      )}
      {showColumn("checkIn") && (
      <td style={{ ...tdStyle, direction: "ltr", fontFamily: "'Courier New', monospace" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
          <span>{record.checkIn || "-"}</span>
          {record.remoteIn
            ? <PunchPin punch={record.remoteIn} kind="in" employeeName={employeeName} theme={theme} language={language} />
            : <PunchLocation name={record.checkInLocationName} theme={theme} language={language} />}
          {record.editedByIn && (
            <span
              title={language === "ar" ? `عدّل بواسطة: ${record.editedByIn}` : `Edited by: ${record.editedByIn}`}
              style={{ fontSize: "10px", color: "#f59e0b", fontFamily: "sans-serif", display: "flex", alignItems: "center", gap: "3px" }}
            >
               {record.editedByIn}
            </span>
          )}
        </div>
      </td>
      )}
      {showColumn("checkOut") && (
      <td style={{ ...tdStyle, direction: "ltr", fontFamily: "'Courier New', monospace" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
          <span>{record.checkOut || "-"}</span>
          {record.remoteOut
            ? <PunchPin punch={record.remoteOut} kind="out" employeeName={employeeName} theme={theme} language={language} />
            : <PunchLocation name={record.checkOutLocationName} theme={theme} language={language} />}
          {record.editedByOut && (
            <span
              title={language === "ar" ? `عدّل بواسطة: ${record.editedByOut}` : `Edited by: ${record.editedByOut}`}
              style={{ fontSize: "10px", color: "#f59e0b", fontFamily: "sans-serif", display: "flex", alignItems: "center", gap: "3px" }}
            >
               {record.editedByOut}
            </span>
          )}
        </div>
      </td>
      )}
      {showColumn("hoursWorked") && (
      <td style={{ ...tdStyle, fontWeight: "500" }}>
        {record.hoursWorked > 0
          ? `${Math.round(parseFloat(record.hoursWorked) * 10) / 10} ${t.hours}`
          : "-"}
      </td>
      )}
      {showColumn("status") && (
      <td style={{ ...tdStyle }}>
        <StatusBadge status={record.status} language={language} />
      </td>
      )}
      {showColumn("overtime") && (
      <td style={{ ...tdStyle, fontWeight: "500" }}>
        {overtimeApprovalRequired && (record.overTime > 0 || record.overTime) ? (
          <OvertimeApprovalButton
            record={record}
            theme={theme}
            isRTL={isRTL}
            language={language}
            viewType={viewType}
            canApproveOvertime={canApproveOvertime}
            overtimeApprovalRequired={overtimeApprovalRequired}
            onApprove={onApproveOvertime}
            onReject={onRejectOvertime}
          />
        ) : (
          getOvertimeDisplay(record.overTime)
        )}
      </td>
      )}
      {showColumn("tardiness") && (
      <td style={{ ...tdStyle, fontWeight: "500" }}>
        {getTardinessDisplay(record.underTime)}
      </td>
      )}
      {showColumn("client") && (
      <td style={tdStyle}>{record.clientName || "-"}</td>
      )}
      {showColumn("actions") && (
      <td style={{ ...tdStyle }}>
        <RowActions
          record={record}
          theme={theme}
          isRTL={isRTL}
          t={t}
          language={language}
          viewType={viewType}
          companyConfigs={companyConfigs}
          canResetCheckIn={canResetCheckIn}
          canResetCheckOut={canResetCheckOut}
          canRevertOriginal={canRevertOriginal}
          canViewAttendance={canViewAttendance}
          // ── NEW ───────────────────────────────────────────────────────────────────
          canApproveOvertime={canApproveOvertime}
          overtimeApprovalRequired={overtimeApprovalRequired}
          onApproveOvertime={onApproveOvertime}
          onRejectOvertime={onRejectOvertime}
          // ─────────────────────────────────────────────────────────────────────────
          getConfigForEmployee={getConfigForEmployee}
          updateAttendancein={updateAttendancein}
          updateAttendanceout={updateAttendanceout}
          revertAttendanceToOriginal={revertAttendanceToOriginal}
          fetchEmployeeHistory={fetchEmployeeHistory}
          showAlert={showAlert}
          BASE_URL={BASE_URL}
        />
      </td>
      )}
    </tr>
  );
};

export default TableRow;